// frontend/src/askos/PerformancePage.js
//
// Full Performance view — same data as PerformanceSnapshotCard.js but with
// a week/month range toggle and a day-by-day activity breakdown.
import React, { useEffect, useState, useCallback } from 'react';
import { getPerformanceSnapshot } from '../api/performanceApi';

const TILES = [
    { key: 'questions_attempted', label: 'Questions Attempted', fmt: v => v },
    { key: 'accuracy_pct', label: 'Accuracy', fmt: v => `${v}%` },
    { key: 'study_time_min', label: 'Study Time', fmt: v => `${v}m` },
    { key: 'topics_covered', label: 'Topics Covered', fmt: v => v },
];

const Sparkline = ({ daily }) => {
    if (!daily || daily.length === 0) return null;
    const max = Math.max(1, ...daily.map(d => d.count));
    const bars = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'];
    const str = daily.map(d => bars[Math.min(bars.length - 1, Math.floor((d.count / max) * (bars.length - 1)))]).join('');
    return <div className="sparkline" style={{ fontSize: 22, marginTop: 16 }}>{str}</div>;
};

const fmtDay = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

const PerformancePage = ({ slug }) => {
    const [range, setRange] = useState('week');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        getPerformanceSnapshot(slug, range).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    }, [slug, range]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="subpage">
            <div className="panel-heading" style={{ marginBottom: 20 }}>
                <h2>Performance</h2>
                <div className="filter-row">
                    <button type="button" className={`filter-chip ${range === 'week' ? 'active' : ''}`} onClick={() => setRange('week')}>This Week</button>
                    <button type="button" className={`filter-chip ${range === 'month' ? 'active' : ''}`} onClick={() => setRange('month')}>This Month</button>
                </div>
            </div>

            {loading && <p className="empty-note">Loading…</p>}

            {!loading && (
                <article className="panel">
                    <div className="metric-grid">
                        {TILES.map(t => {
                            const metric = data?.[t.key];
                            return (
                                <div key={t.key} className="metric-card">
                                    <span>{t.label}</span>
                                    <strong>{metric ? t.fmt(metric.value) : '—'}</strong>
                                    {metric && (
                                        <small>{metric.delta_pct >= 0 ? '↑' : '↓'} {Math.abs(metric.delta_pct)}% vs prior {range}</small>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <Sparkline daily={data?.daily} />
                </article>
            )}

            {!loading && data?.daily?.length > 0 && (
                <article className="panel" style={{ marginTop: 20 }}>
                    <div className="panel-heading"><h3>Daily Activity</h3></div>
                    {data.daily.map(d => (
                        <div key={d.date} className="study-row">
                            <div className={`status${d.count > 0 ? ' done' : ' muted'}`}>{d.count > 0 ? d.count : ''}</div>
                            <div className="study-info">
                                <strong>{fmtDay(d.date)}</strong>
                                <span>{d.count} activit{d.count === 1 ? 'y' : 'ies'}</span>
                            </div>
                        </div>
                    ))}
                </article>
            )}
        </div>
    );
};

export default PerformancePage;
